import { LayoutService } from 'src/app/core/services/layout.service';
import { PatientService } from 'src/app/core/services/patient.service';
import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineLatest, BehaviorSubject } from 'rxjs';
import { take, switchMap, map, shareReplay } from 'rxjs/operators';
import { Patient } from 'src/app/core/models/patient.model';
import { AuthenticationService } from 'src/app/core/services/authentication.service';
import { CabinetService } from 'src/app/core/services/cabinet.service';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-dialog-patient',
  templateUrl: './dialog-patient.component.html',
  styleUrls: ['./dialog-patient.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DialogPatientComponent implements OnInit, AfterViewInit {

  form = this.getForm();
  disableAnimation = true;

  showAdditionalInfo = this.getShowAdditionalInfo();
  showContactInfo = this.getShowContactInfoInfo();
  loading$ = new BehaviorSubject(false);
  activateHandsetLayout$ = this.layoutService.activateHandsetLayout$;

  constructor(
    private dialogRef: MatDialogRef<DialogPatientComponent>,
    @Inject(MAT_DIALOG_DATA) public data: Patient | null,
    private formBuilder: FormBuilder,
    private cabinetService: CabinetService,
    private authenticationService: AuthenticationService,
    private patientService: PatientService,
    private router: Router,
    private snackBar: MatSnackBar,
    private layoutService: LayoutService
  ) { }

  ngOnInit(): void {
  }

  getForm(): FormGroup {
    return this.formBuilder.group({
      id: this.data?.id,
      profilePicture: this.data?.profilePicture,
      name: this.data?.name,
      ownerName: this.data?.ownerName,
      species: this.data?.species,
      breed: this.data?.breed,
      weight: this.data?.weight,
      birthDate: this.data?.birthDate,
      gender: this.data?.gender,
      ownerEmail: [this.data?.ownerEmail, [Validators.email]],
      ownerPhoneNumber: this.data?.ownerPhoneNumber
    });
  }

  save() {
    this.loading$.next(true);
    if (this.form.value.id) {
      this.updatePatient();
    } else {
      this.addPatient();
    }
  }

  private updatePatient() {
    this.patientService.updatePatient({
      id: this.form.value.id,
      changes: this.form.value
    }).subscribe(
      () => {
        this.loading$.next(false);
        this.dialogRef.close();
      },
      error => {
        this.snackBar.open(`${error}`, 'Dismiss');
      }
    );
  }

  private addPatient() {
    combineLatest([
      this.cabinetService.cabinetId$,
      this.authenticationService.user$
    ]).pipe(
      take(1),
      switchMap(([cabinetId, user]) => this.patientService.addPatient({
        ...this.form.value,
        cabinetId,
        createdAt: Date.now(),
        createdBy: {
          userId: user.uid,
          userName: user.displayName
        }
      }))
    ).subscribe(
      docRef => {
        this.loading$.next(false);
        this.router.navigateByUrl(`/patients/${docRef.id}`);
        this.dialogRef.close();
      },
      error => {
        this.snackBar.open(`${error}`, 'Dismiss');
      });
  }

  // angular material issue
  // https://github.com/angular/components/issues/13870
  ngAfterViewInit(): void {
    // timeout required to avoid the dreaded 'ExpressionChangedAfterItHasBeenCheckedError'
    setTimeout(() => this.disableAnimation = false);
  }

  private getShowAdditionalInfo(): boolean {
    return !!(this.data?.birthDate || this.data?.weight || this.data?.gender);
  }

  private getShowContactInfoInfo(): boolean {
    return !!(this.data?.ownerEmail || this.data?.ownerPhoneNumber);
  }

}
